import { useState, useEffect } from 'react';
import { apiGet } from './globalFunctions';

export const AFFILIATE_PERMS_CACHE_KEY = 'affiliatePermissionsCache';
export const AFFILIATE_PERMS_EVENT = 'affiliatePermissionsChanged';

// Detects affiliate status directly from the JWT payload — no backend required.
// JWT structure: { id: rootId | userId, user: actualUserId }
// Affiliate iff id !== user (id = rootId, user = affiliateUserId).
const isAffiliateFromToken = () => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.user === 'number' && payload.id !== payload.user;
  } catch { return false; }
};

// Synchronous read for non-React code (items.js, PermissionGuard).
// Returns { isAffiliate, permissions } from cache.
// Defaults: isAffiliate=false, permissions=[] (root user — full access).
export const getAffiliatePermissions = () => {
  try {
    const cached = localStorage.getItem(AFFILIATE_PERMS_CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch {}
  return { isAffiliate: false, permissions: [] };
};

export const hasAffiliatePermission = (permissionKey) => {
  const { isAffiliate, permissions } = getAffiliatePermissions();
  if (!isAffiliate) return true;
  return permissions.includes(permissionKey);
};

// Fetches /permissions/mine, writes to localStorage and fires the event.
export const fetchAndCacheAffiliatePermissions = () =>
  new Promise((resolve) => {
    apiGet('/permissions/mine', {
      done: (resp) => {
        const data = { isAffiliate: resp.data.isAffiliate, permissions: resp.data.permissions };
        localStorage.setItem(AFFILIATE_PERMS_CACHE_KEY, JSON.stringify(data));
        window.dispatchEvent(new Event(AFFILIATE_PERMS_EVENT));
        resolve(data);
      },
      error: () => {
        // /permissions/mine indisponível (ex: servidor sem o endpoint) —
        // usa JWT para detectar isAffiliate sem depender do backend.
        const data = { isAffiliate: isAffiliateFromToken(), permissions: [] };
        localStorage.setItem(AFFILIATE_PERMS_CACHE_KEY, JSON.stringify(data));
        window.dispatchEvent(new Event(AFFILIATE_PERMS_EVENT));
        resolve(data);
      },
    });
  });

// React hook — keeps local state in sync with the cache event.
export const useAffiliatePermissions = () => {
  const [perms, setPerms] = useState(getAffiliatePermissions);

  useEffect(() => {
    const sync = () => setPerms(getAffiliatePermissions());
    window.addEventListener(AFFILIATE_PERMS_EVENT, sync);
    return () => window.removeEventListener(AFFILIATE_PERMS_EVENT, sync);
  }, []);

  return perms;
};
